import { Injectable } from '@angular/core';
import { of, Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BankDetails, UserBanks } from 'src/app/models/bank-details';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class UserBanksService {
  private bankFieldsQuery = `
    id
    bankName
    iban
    accountNumber
    branchName
    branchAddress
    swiftCode
    accountHolderLegalName
    isComplete
    isDefault
  `;

  private _maxBanksNumberReachedRequest: Observable<boolean> | null = null;

  constructor(private api: ApiService) {}

  getDefaultBank(): Observable<BankDetails> {
    return this.api.get(`{
          profile{
            bankDetails{
              ${this.bankFieldsQuery}
            }
          }
        }`).pipe(
      switchMap(response => {
        const data = response['data']['profile'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data.bankDetails);
      }),
      this.api.unifyErrors()
    );
  }

  getBanks(): Observable<UserBanks> {
    return this.api.get(`{
      profile{
        banks{
          ${this.bankFieldsQuery}
        }
        maximumAllowedUserBanks
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['profile'];

        if (data['errors']) {
          return throwError(data);
        }

        return of({ banks: data.banks, maximumAllowedUserBanks: data.maximumAllowedUserBanks });
      }),
      this.api.unifyErrors()
    );
  }

  createUserBankDetails(bankDetails: BankDetails, otpCode: string): Observable<BankDetails> {
    return this.api.get(`mutation createUserBankDetails($bankDetails: BankDetailsInput!, $otpCode: String!) {
      createUserBankDetails(bankDetails: $bankDetails, otpCode: $otpCode) {
        ... on BankDetails {${this.bankFieldsQuery}}
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      bankDetails,
      otpCode
    }).pipe(
      switchMap(response => {
        const data = response['data']['createUserBankDetails'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  deleteUserBankDetails(id: number): Observable<boolean> {
    return this.api.get(`mutation deleteUserBankDetails($id: ID!) {
      deleteUserBankDetails(id: $id) {
         ... on GeneralResponse {
          success
         }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      id
    }).pipe(
      switchMap(response => {
        const data = response['data']['deleteUserBankDetails'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  setDefaultUserBank(id: number): Observable<boolean> {
    return this.api.get(`mutation setDefaultUserBank($id: ID!) {
      setDefaultUserBank(id: $id) {
         ... on GeneralResponse {
          success
         }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      id
    }).pipe(
      switchMap(response => {
        const data = response['data']['setDefaultUserBank'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  maxBanksNumberReached(forceRefresh = false): Observable<boolean> {
    if (this._maxBanksNumberReachedRequest && !forceRefresh) {
      return this._maxBanksNumberReachedRequest;
    }
    
    this._maxBanksNumberReachedRequest = this.getBanks().pipe(
      switchMap(data => {
        if (data['errors']) {
          return throwError(data);
        }
        return of(data?.maximumAllowedUserBanks > data?.banks?.length);
      }),
      this.api.unifyErrors()
    );

    return this._maxBanksNumberReachedRequest;
  }

  clearCache() {
    this._maxBanksNumberReachedRequest = null;
  }
}
